'use client'
import type { FC } from 'react'
import { Suspense } from 'react'
import { type ProductListWidgetType, type StorefrontWidget } from 'ecosystem'
import { parseIntSafe } from 'shared-utils'
import { getAspectRatioAsNumber } from 'shared-utils/utils/aspectRatio'
import ProductListVertical, {
  type IProductListVertical
} from '../components/product-list-vertical/ProductListVertical'
import useProductList from '../hooks/useProductList'
import { type WidgetImplementation } from '../../widgets/registry/typings'

type ProductListVerticalWidgetProps = WidgetImplementation<
  StorefrontWidget<ProductListWidgetType>
> &
  Pick<
    IProductListVertical,
    | 'cartItems'
    | 'onAddToCart'
    | 'productsState'
    | 'onMore'
    | 'onSearchUpdate'
    | 'sortingOptions'
    | 'sliderMaxPrice'
    | 'sliderDefaultValues'
    | 'onProductRequest'
    | 'showsPrice'
    | 'customComponents'
  > & {
    paths: {
      getInitialProducts: string
      productsUrlPath: string
    }
  }

const ProductListVerticalWidget: FC<ProductListVerticalWidgetProps> = ({
  productsState,
  sortingOptions,
  sliderMaxPrice,
  onAddToCart,
  cartItems,
  onMore,
  onSearchUpdate,
  paths,
  onProductRequest,
  sliderDefaultValues,
  dynamicProps,
  customComponents,
  showsPrice,
  widget,
  widgetId
}) => {
  const { products, totalElements, loadMore, isLoading, isNextPageAvailable } = useProductList({
    id: dynamicProps?.viewId || '',
    type:
      (dynamicProps?.type.toLowerCase() as 'category' | 'brand' | 'tag' | undefined) || 'category',
    filterConfig: dynamicProps?.filterConfig || {
      brands: [],
      options: []
    },
    productsState,
    getInitialProducts: paths.getInitialProducts,
    sortingOptions
  })

  return (
    <ProductListVertical
      deliveryOptions={dynamicProps?.deliveryOptions ?? null}
      infiniteScrollConfig={{
        loadMore,
        shouldLoadMore: isNextPageAvailable
      }}
      items={products}
      productsUrlPath={paths.productsUrlPath}
      py={0}
      sectionId={widgetId}
      title=""
      totalItems={totalElements}
      columnsNumber={parseIntSafe(widget.widget.columnsNumber)}
      columnsNumberTablet={parseIntSafe(widget.widget.columnsNumberTablet)}
      columnsNumberMobile={parseIntSafe(widget.widget.columnsNumberMobile)}
      imageAspectRatio={getAspectRatioAsNumber(widget.widget.imageAspectRatio)}
      imageAspectRatioTablet={getAspectRatioAsNumber(widget.widget.imageAspectRatioTablet)}
      imageAspectRatioMobile={getAspectRatioAsNumber(widget.widget.imageAspectRatioMobile)}
      {...{
        isLoading,
        productsState,
        sortingOptions,
        onAddToCart,
        cartItems,
        onMore,
        onSearchUpdate,
        sliderMaxPrice,
        onProductRequest,
        sliderDefaultValues,
        customComponents,
        showsPrice
      }}
    />
  )
}

// https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
const ProductListVerticalWidgetSuspended: FC<ProductListVerticalWidgetProps> = (props) => {
  const {
    productsState,
    sortingOptions,
    sliderMaxPrice,
    onAddToCart,
    cartItems,
    onMore,
    onSearchUpdate,
    paths,
    onProductRequest,
    sliderDefaultValues,
    dynamicProps,
    customComponents,
    showsPrice,
    widget,
    widgetId
  } = props

  return (
    <Suspense
      fallback={
        <ProductListVertical
          deliveryOptions={dynamicProps?.deliveryOptions ?? null}
          infiniteScrollConfig={{
            loadMore: () => void 0,
            shouldLoadMore: false
          }}
          items={[]}
          productsUrlPath={paths.productsUrlPath}
          py={0}
          sectionId={widgetId}
          title=""
          totalItems={0}
          columnsNumber={parseIntSafe(widget.widget.columnsNumber)}
          columnsNumberTablet={parseIntSafe(widget.widget.columnsNumberTablet)}
          columnsNumberMobile={parseIntSafe(widget.widget.columnsNumberMobile)}
          imageAspectRatio={getAspectRatioAsNumber(widget.widget.imageAspectRatio)}
          imageAspectRatioTablet={getAspectRatioAsNumber(widget.widget.imageAspectRatioTablet)}
          imageAspectRatioMobile={getAspectRatioAsNumber(widget.widget.imageAspectRatioMobile)}
          {...{
            isLoading: true,
            productsState,
            sortingOptions,
            onAddToCart,
            cartItems,
            onMore,
            onSearchUpdate,
            sliderMaxPrice,
            onProductRequest,
            sliderDefaultValues,
            customComponents,
            showsPrice
          }}
        />
      }
    >
      <ProductListVerticalWidget {...props} />
    </Suspense>
  )
}

export default ProductListVerticalWidgetSuspended
